import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./invoice.css";

export default function InvoicePage() {
    const school = JSON.parse(localStorage.getItem("schoolAdmin"));

    const [invoices, setInvoices] = useState([]);
    const [showForm, setShowForm] = useState(false);

    const [form, setForm] = useState({
        student_name: "",
        student_class: "",
        phone: "",
        email: "",
        discount: 0,
        tax: 0,
        payment_method: "",
    });

    const [items, setItems] = useState([
        { description: "", amount: "" },
    ]);

    const [total, setTotal] = useState(0);

    // =========================
    // LOAD
    // =========================
    useEffect(() => {
        loadInvoices();
    }, []);

    const loadInvoices = async () => {
        try {
            const res = await API.get("/school-admin/invoices/", {
                headers: { "X-SCHOOL-ID": school.school.id },
            });
            setInvoices(res.data.invoices || []);
        } catch {
            console.log("No invoices");
        }
    };

    // =========================
    // CALCULATION
    // =========================
    const calculateTotal = (data, formData = form) => {
        let subtotal = 0;

        data.forEach((i) => {
            subtotal += Number(i.amount || 0);
        });

        const finalTotal =
            subtotal -
            Number(formData.discount || 0) +
            Number(formData.tax || 0);

        setTotal(finalTotal);
    };

    // AUTO UPDATE TOTAL
    useEffect(() => {
        calculateTotal(items);
    }, [items, form.discount, form.tax]);

    // =========================
    // ITEMS
    // =========================
    const addItem = () => {
        setItems([...items, { description: "", amount: "" }]);
    };

    const removeItem = (i) => {
        const updated = items.filter((_, index) => index !== i);
        setItems(updated);
    };

    const handleItemChange = (i, field, value) => {
        const updated = [...items];
        updated[i][field] = value;
        setItems(updated);
    };

    // =========================
    // CREATE
    // =========================
    const generateInvoice = async () => {
        if (!form.student_name) return alert("Enter student name");

        for (let item of items) {
            if (!item.description || !item.amount) {
                return alert("Fill all item fields");
            }
        }

        try {
            await API.post(
                "/school-admin/invoice/create/",
                {
                    ...form,
                    discount: Number(form.discount || 0),
                    tax: Number(form.tax || 0),
                    items: items.map((i) => ({
                        description: i.description,
                        amount: Number(i.amount || 0),
                    })),
                },
                { headers: { "X-SCHOOL-ID": school.school.id } }
            );

            alert("✅ Invoice Created");

            setShowForm(false);

            // RESET
            setForm({
                student_name: "",
                student_class: "",
                phone: "",
                email: "",
                discount: 0,
                tax: 0,
                payment_method: "",
            });

            setItems([{ description: "", amount: "" }]);
            setTotal(0);

            loadInvoices();
        } catch {
            alert("❌ Failed");
        }
    };

    // =========================
    // DOWNLOAD
    // =========================
    const downloadInvoice = async (id) => {
        try {
            const res = await API.get(
                `/school-admin/invoice/download/${id}/`,
                {
                    responseType: "blob",
                    headers: { "X-SCHOOL-ID": school.school.id },
                }
            );

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = `invoice_${id}.pdf`;
            link.click();
        } catch {
            alert("Download failed");
        }
    };

    // =========================
    // UI
    // =========================
    return (
        <div className="invoice-page invoice-list-page">

            <div className="invoice-hero">
                <div>
                    <span>Fees invoices</span>
                    <h2>Invoices</h2>
                    <p>Create, track, and download student fee invoices in one place.</p>
                </div>
                <button onClick={() => setShowForm(true)}>
                    + Create Invoice
                </button>
            </div>

            {/* 📄 LIST */}
            <div className="invoice-list">
                <h3>Invoices</h3>

                {invoices.length === 0 && (
                    <div className="invoice-empty">
                        <div className="empty-icon">₹</div>
                        <h4>No invoices yet</h4>
                        <p>Create the first invoice to start building the fee record.</p>
                    </div>
                )}

                {invoices.map((inv) => (
                    <div key={inv.id} className="invoice-item">
                        <span>{inv.student_name} - ₹{inv.total}</span>
                        <button onClick={() => downloadInvoice(inv.id)}>
                            Download
                        </button>
                    </div>
                ))}
            </div>

            {/* 🧾 MODAL */}
            {showForm && (
                <div className="modal">
                    <div className="modal-box">

                        <h3>Create Invoice</h3>

                        <input placeholder="Student Name"
                            value={form.student_name}
                            onChange={(e) => setForm({ ...form, student_name: e.target.value })}
                        />

                        <input placeholder="Class"
                            value={form.student_class}
                            onChange={(e) => setForm({ ...form, student_class: e.target.value })}
                        />

                        <input placeholder="Phone"
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        />

                        <input placeholder="Email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                        />

                        <h4>Items</h4>

                        {items.map((item, i) => (
                            <div key={i} className="item-row">
                                <input
                                    placeholder="Description"
                                    value={item.description}
                                    onChange={(e) => handleItemChange(i, "description", e.target.value)}
                                />

                                <input
                                    type="number"
                                    placeholder="Amount"
                                    value={item.amount}
                                    onChange={(e) => handleItemChange(i, "amount", e.target.value)}
                                />

                                <button onClick={() => removeItem(i)}>❌</button>
                            </div>
                        ))}

                        <button onClick={addItem}>+ Add Row</button>

                        <h4>Summary</h4>

                        <input
                            type="number"
                            placeholder="Discount (e.g. 100)"
                            value={form.discount || ""}
                            onChange={(e) =>
                                setForm({ ...form, discount: e.target.value })
                            }
                        />

                        <input
                            type="number"
                            placeholder="Tax (e.g. 50)"
                            value={form.tax || ""}
                            onChange={(e) =>
                                setForm({ ...form, tax: e.target.value })
                            }
                        />
                        <input placeholder="Payment Method"
                            value={form.payment_method}
                            onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                        />

                        <h3>Total: ₹{total}</h3>

                        <div className="modal-actions">
                            <button onClick={generateInvoice}>Generate</button>
                            <button onClick={() => setShowForm(false)}>Cancel</button>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
}
